/**
 * Henderson-Hasselbalch pH Adjustment Calculator
 * 
 * Implements professional pH adjustment calculations based on:
 * - Henderson-Hasselbalch equation: pH = pKa + log([A-]/[HA])
 * - Acid/base addition calculations
 * - Buffer capacity analysis
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider
} from '@mui/material';
import Grid from '../common/Grid';
import {
  Calculate as CalculateIcon,
  Science as ScienceIcon,
  Warning as WarningIcon,
  TuneRounded as TuneIcon
} from '@mui/icons-material';

interface PHAdjustmentAgent {
  name: string;
  formula: string;
  pKa: number;
  molarity: number;
  density: number; // g/mL
  cost: number; // per liter
  type: 'acid' | 'base';
}

interface PHAdjustmentResult {
  targetPH: number;
  currentPH: number;
  requiredAgent: PHAdjustmentAgent;
  volumeRequired: number; // mL
  finalPH: number;
  bufferCapacity: number;
  warnings: string[];
  calculations: {
    initialH: number;
    targetH: number;
    deltaH: number;
    agentMoles: number;
    agentVolume: number;
  };
}

interface HendersonHasselbalchCalculatorProps {
  currentPH: number;
  solutionVolume: number; // Liters
  ionicStrength: number;
  dilutionRatio?: number; // e.g., 100 for 1:100 dilution
  onPHAdjustmentCalculated: (result: PHAdjustmentResult) => void;
}

const HendersonHasselbalchCalculator: React.FC<HendersonHasselbalchCalculatorProps> = ({
  currentPH,
  solutionVolume,
  ionicStrength,
  dilutionRatio = 100,
  onPHAdjustmentCalculated
}) => {
  const [targetPH, setTargetPH] = useState<number>(6.0);
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [adjustmentResult, setAdjustmentResult] = useState<PHAdjustmentResult | null>(null);
  const [calculating, setCalculating] = useState(false);
  const temperature = 25; // Fixed temperature for now

  // Validate props to prevent crashes
  if (!currentPH || !solutionVolume || currentPH < 0 || currentPH > 14) {
    return (
      <Alert severity="error">
        <Typography variant="body2">
          Invalid pH adjustment parameters. Please ensure valid pH and solution volume are provided.
        </Typography>
      </Alert>
    );
  }

  // Professional pH adjustment agents (enhanced with more options)
  const phAdjustmentAgents: PHAdjustmentAgent[] = [
    // Acids - Professional Grade
    {
      name: 'Phosphoric Acid (85%)',
      formula: 'H3PO4',
      pKa: 2.15, // First dissociation
      molarity: 14.7, // 85% solution
      density: 1.685,
      cost: 15.0,
      type: 'acid'
    },
    {
      name: 'Nitric Acid (68%)',
      formula: 'HNO3',
      pKa: -1.3,
      molarity: 15.8, // 68% solution
      density: 1.41,
      cost: 12.0,
      type: 'acid'
    },
    {
      name: 'Sulfuric Acid (96%)',
      formula: 'H2SO4',
      pKa: -3.0, // First dissociation
      molarity: 18.0, // 96% solution
      density: 1.84,
      cost: 10.0,
      type: 'acid'
    },
    {
      name: 'Citric Acid (50%)',
      formula: 'C6H8O7',
      pKa: 3.13, // First dissociation
      molarity: 2.6, // 50% solution
      density: 1.22,
      cost: 18.0,
      type: 'acid'
    },
    // Bases - Professional Grade
    {
      name: 'Potassium Hydroxide (45%)',
      formula: 'KOH',
      pKa: 15.7, // pKw - pKb
      molarity: 11.7, // 45% solution
      density: 1.43,
      cost: 20.0,
      type: 'base'
    },
    {
      name: 'Sodium Hydroxide (50%)',
      formula: 'NaOH',
      pKa: 15.7, // pKw - pKb
      molarity: 19.1, // 50% solution
      density: 1.53,
      cost: 15.0,
      type: 'base'
    },
    {
      name: 'Potassium Carbonate (40%)',
      formula: 'K2CO3',
      pKa: 10.3, // Second dissociation of H2CO3
      molarity: 2.9, // 40% solution
      density: 1.43,
      cost: 25.0,
      type: 'base'
    }
  ];

  /**
   * Calculate pH adjustment using Henderson-Hasselbalch equation
   */
  const calculatePHAdjustment = async () => {
    setCalculating(true);
    
    try {
      const agentIndex = parseInt(selectedAgent.replace('agent_', ''));
      const agent = phAdjustmentAgents[agentIndex];
      if (!agent) {
        throw new Error('Selected pH adjustment agent not found');
      }

      // Simplified practical pH adjustment calculation
      const pHDifference = targetPH - currentPH; // Positive = need base, Negative = need acid

      // Practical approach: estimate moles needed based on pH change and solution volume
      let agentMoles: number;

      if (agent.type === 'acid' && pHDifference < 0) {
        // Need to lower pH (add acid)
        const pHDrop = Math.abs(pHDifference);
        // Empirical formula: moles ≈ pH_change × volume × buffer_factor × 0.001
        const bufferFactor = 1.0 + (ionicStrength * 10); // Simple buffer factor
        agentMoles = pHDrop * solutionVolume * bufferFactor * 0.001;

        // For strong acids like HNO3, reduce the amount needed
        if (agent.formula === 'HNO3' || agent.formula === 'H2SO4') {
          agentMoles *= 0.5; // Strong acids are more effective
        }
      } else if (agent.type === 'base' && pHDifference > 0) {
        // Need to raise pH (add base)
        const pHRise = Math.abs(pHDifference);
        const bufferFactor = 1.0 + (ionicStrength * 10);
        agentMoles = pHRise * solutionVolume * bufferFactor * 0.001;

        // For strong bases, reduce the amount needed
        if (agent.formula === 'KOH' || agent.formula === 'NaOH') {
          agentMoles *= 0.5;
        }
      } else {
        // Wrong agent type for the pH change needed
        agentMoles = 0;
      }

      // Convert moles to volume (mL)
      const agentVolume = (agentMoles / agent.molarity) * 1000; // mL

      // Calculate final pH (simplified approach)
      const finalVolume = solutionVolume + (agentVolume / 1000);

      // For small pH adjustments, use linear approximation
      const pHDrop = Math.abs(pHDifference);
      let finalPH: number;

      if (agent.type === 'acid' && pHDifference < 0) {
        // Lowering pH with acid - use practical approximation
        const efficiency = 0.8; // Typical efficiency for small adjustments
        finalPH = currentPH - (pHDrop * efficiency);
      } else if (agent.type === 'base' && pHDifference > 0) {
        // Raising pH with base
        const efficiency = 0.8;
        finalPH = currentPH + (pHDrop * efficiency);
      } else {
        // Wrong agent type
        finalPH = currentPH;
      }

      // Ensure reasonable bounds
      finalPH = Math.max(1.0, Math.min(13.0, finalPH));

      // Calculate buffer capacity
      const bufferCapacity = calculateBufferCapacity(finalPH, ionicStrength);

      // Generate warnings
      const warnings = generateWarnings(agentVolume, finalPH, agent, solutionVolume);

      // Calculate values for debugging/display
      const initialH = Math.pow(10, -currentPH);
      const targetH = Math.pow(10, -targetPH);
      const deltaH = targetH - initialH;

      const result: PHAdjustmentResult = {
        targetPH,
        currentPH,
        requiredAgent: agent,
        volumeRequired: Math.max(0, agentVolume),
        finalPH,
        bufferCapacity,
        warnings,
        calculations: {
          initialH,
          targetH,
          deltaH,
          agentMoles,
          agentVolume
        }
      };

      setAdjustmentResult(result);
      // Don't auto-complete - let user review and proceed manually

    } catch (error) {
      console.error('pH adjustment calculation failed:', error);
    } finally {
      setCalculating(false);
    }
  };

  /**
   * Calculate activity coefficient using Davies equation
   */
  const calculateActivityCoefficient = (I: number): number => {
    // Davies equation: log γ = -A × z² × (√I / (1 + √I) - 0.3 × I)
    const A = 0.5085; // at 25°C
    const z = 1; // charge of H+
    return Math.pow(10, -A * z * z * (Math.sqrt(I) / (1 + Math.sqrt(I)) - 0.3 * I));
  };

  /**
   * Calculate buffer factor using Van Slyke equation
   * β = 2.303 × ([H+] + [OH-] + Σ(αi × αj × Ci × zi²))
   */
  const calculateBufferFactor = (currentPH: number, targetPH: number): number => {
    const H = Math.pow(10, -currentPH);
    const OH = Math.pow(10, -(14 - currentPH));

    // Base buffer capacity from water
    let bufferCapacity = 2.303 * (H + OH);

    // Add contribution from nutrient salts (simplified)
    // In a real system, this would analyze all buffer systems present
    const nutrientBufferContribution = ionicStrength * 0.05; // Empirical factor
    bufferCapacity += nutrientBufferContribution;

    // Add phosphate buffer system if present (common in hydroponic solutions)
    const phosphateContribution = calculatePhosphateBuffer(currentPH);
    bufferCapacity += phosphateContribution;

    // Simplified practical buffer factor
    const pHDifference = Math.abs(targetPH - currentPH);
    let bufferFactor = 1.0;

    // Increase factor for larger pH changes
    if (pHDifference > 0.5) bufferFactor += 0.5;
    if (pHDifference > 1.0) bufferFactor += 1.0;
    if (pHDifference > 2.0) bufferFactor += 2.0;

    // Account for ionic strength
    bufferFactor += ionicStrength * 2;

    return bufferFactor;
  };

  /**
   * Calculate phosphate buffer contribution
   * H3PO4 ⇌ H2PO4⁻ ⇌ HPO4²⁻ ⇌ PO4³⁻
   */
  const calculatePhosphateBuffer = (pH: number): number => {
    // Typical phosphate concentration in hydroponic solutions: ~1-5 mM
    const totalPhosphate = 0.003; // mol/L (3 mM)

    // Phosphoric acid pKa values
    const pKa1 = 2.15;
    const pKa2 = 7.20;
    const pKa3 = 12.35;

    const H = Math.pow(10, -pH);
    const Ka1 = Math.pow(10, -pKa1);
    const Ka2 = Math.pow(10, -pKa2);
    const Ka3 = Math.pow(10, -pKa3);

    // Calculate alpha fractions
    const denominator = H*H*H + H*H*Ka1 + H*Ka1*Ka2 + Ka1*Ka2*Ka3;
    const alpha0 = H*H*H / denominator;
    const alpha1 = H*H*Ka1 / denominator;
    const alpha2 = H*Ka1*Ka2 / denominator;
    const alpha3 = Ka1*Ka2*Ka3 / denominator;

    // Buffer capacity contribution
    const ln10 = Math.log(10);
    const bufferContribution = 2.303 * totalPhosphate * (
      alpha0 * alpha1 + 4 * alpha1 * alpha2 + 9 * alpha2 * alpha3
    );

    return bufferContribution;
  };

  /**
   * Calculate final pH using iterative Henderson-Hasselbalch equation
   * Accounts for temperature, ionic strength, and buffer systems
   */
  const calculateFinalPH = (
    initialPH: number,
    targetPH: number,
    agentMoles: number,
    agent: PHAdjustmentAgent,
    finalVolume: number
  ): number => {
    // Temperature correction (25°C reference)
    const tempCorrection = (temperature - 25) * 0.003; // pH units per °C

    // Initial concentrations
    let H_concentration = Math.pow(10, -initialPH);
    let OH_concentration = Math.pow(10, -(14 - initialPH));

    // Add agent contribution
    const agentConcentration = agentMoles / finalVolume; // mol/L

    if (agent.type === 'acid') {
      // For acids: increase H+ concentration
      if (agent.formula === 'H3PO4') {
        // Phosphoric acid - triprotic
        const alpha0 = calculateAlpha0(agent.pKa, initialPH);
        H_concentration += agentConcentration * (1 + alpha0 * 2); // Simplified
      } else if (agent.formula === 'H2SO4') {
        // Sulfuric acid - diprotic (first dissociation complete)
        H_concentration += agentConcentration * 2;
      } else {
        // Monoprotic acids
        const dissociation = calculateDissociation(agent.pKa, initialPH);
        H_concentration += agentConcentration * dissociation;
      }
    } else {
      // For bases: increase OH- concentration
      if (agent.formula === 'K2CO3') {
        // Carbonate - diprotic base
        const alpha2 = calculateAlpha2(10.3, initialPH); // pKa2 of H2CO3
        OH_concentration += agentConcentration * (1 + alpha2);
      } else {
        // Monoprotic bases
        OH_concentration += agentConcentration;
      }
    }

    // Recalculate pH from ion concentrations
    const Kw = 1e-14; // Water dissociation constant at 25°C
    H_concentration = Math.sqrt(H_concentration * H_concentration + 4 * Kw) / 2;

    let finalPH = -Math.log10(H_concentration);

    // Apply temperature correction
    finalPH += tempCorrection;

    // Apply ionic strength correction
    const activityCorrection = calculateActivityCoefficient(ionicStrength);
    finalPH += Math.log10(activityCorrection);

    // Ensure reasonable bounds
    return Math.max(1.0, Math.min(13.0, finalPH));
  };

  /**
   * Calculate alpha0 for triprotic acid (H3PO4)
   */
  const calculateAlpha0 = (pKa1: number, pH: number): number => {
    const Ka1 = Math.pow(10, -pKa1);
    const H = Math.pow(10, -pH);
    return H * H / (H * H + H * Ka1 + Ka1 * Ka1);
  };

  /**
   * Calculate alpha2 for diprotic base (CO3²⁻)
   */
  const calculateAlpha2 = (pKa2: number, pH: number): number => {
    const Ka2 = Math.pow(10, -pKa2);
    const H = Math.pow(10, -pH);
    return Ka2 / (H * H + H * Ka2 + Ka2);
  };

  /**
   * Calculate acid dissociation fraction
   */
  const calculateDissociation = (pKa: number, pH: number): number => {
    const Ka = Math.pow(10, -pKa);
    const H = Math.pow(10, -pH);
    return Ka / (H + Ka);
  };

  /**
   * Calculate buffer capacity
   */
  const calculateBufferCapacity = (pH: number, ionicStrength: number): number => {
    // Buffer capacity β = 2.303 × ([H+] + [OH-] + Σ(αi × αj × Ci))
    const H = Math.pow(10, -pH);
    const OH = Math.pow(10, -(14 - pH));
    const baseCapacity = 2.303 * (H + OH);
    
    // Add ionic strength contribution
    const ionicContribution = ionicStrength * 0.1;
    
    return (baseCapacity + ionicContribution) * 1000; // Convert to mmol/L/pH
  };

  /**
   * Generate professional warnings for pH adjustment
   */
  const generateWarnings = (
    volume: number,
    finalPH: number,
    agent: PHAdjustmentAgent,
    solutionVolume: number
  ): string[] => {
    const warnings: string[] = [];

    // Volume warnings
    if (volume > solutionVolume * 10) { // More than 1% of solution volume
      warnings.push('Large volume of pH agent required - consider using more concentrated solution');
    }

    // pH accuracy warnings
    if (Math.abs(finalPH - targetPH) > 0.2) {
      warnings.push('Final pH may deviate from target due to buffer effects - consider multiple small additions');
    }

    // Critical pH range warnings
    if (agent.type === 'acid' && finalPH < 4.0) {
      warnings.push('⚠️ CRITICAL: Very low pH may cause nutrient lockout and root damage');
    }

    if (agent.type === 'base' && finalPH > 8.0) {
      warnings.push('⚠️ CRITICAL: High pH may cause nutrient precipitation and deficiencies');
    }

    // Optimal hydroponic pH range
    if (finalPH < 5.5 || finalPH > 6.5) {
      warnings.push('pH outside optimal hydroponic range (5.5-6.5) - nutrient uptake may be affected');
    }

    // Agent-specific warnings
    if (agent.formula === 'H2SO4' && volume > 1.0) {
      warnings.push('Sulfuric acid adds sulfur - monitor total sulfur levels to prevent toxicity');
    }

    if (agent.formula === 'HNO3' && volume > 2.0) {
      warnings.push('Nitric acid adds nitrogen - adjust base nutrient accordingly to maintain N balance');
    }

    if (agent.formula === 'H3PO4' && volume > 5.0) {
      warnings.push('Phosphoric acid adds phosphorus - monitor P levels to prevent nutrient imbalance');
    }

    if (agent.formula === 'NaOH' && volume > 0.5) {
      warnings.push('Sodium hydroxide adds sodium - monitor Na levels as excess can cause toxicity');
    }

    // Temperature warnings
    if (temperature < 15 || temperature > 30) {
      warnings.push('Temperature outside optimal range (15-30°C) - pH calculations may be less accurate');
    }

    // Buffer capacity warnings
    if (ionicStrength > 0.1) {
      warnings.push('High ionic strength detected - pH adjustment may require more agent than calculated');
    }

    return warnings;
  };

  // Removed auto-calculation to prevent auto-completion
  // User must manually click "Calculate pH Adjustment" button

  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <ScienceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Henderson-Hasselbalch pH Adjustment
          </Typography>

          <Grid container spacing={3}>
            {/* Input Controls */}
            <Grid xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Current pH"
                  type="number"
                  value={currentPH}
                  disabled
                  inputProps={{ step: 0.1, min: 1, max: 14 }}
                  helperText="From nutrient solution calculation"
                />

                <TextField
                  label="Target pH"
                  type="number"
                  value={targetPH}
                  onChange={(e) => setTargetPH(parseFloat(e.target.value) || 6.0)}
                  inputProps={{ step: 0.1, min: 1, max: 14 }}
                  helperText="Desired final pH (typically 5.5-6.5)"
                />

                <FormControl>
                  <InputLabel>pH Adjustment Agent</InputLabel>
                  <Select
                    value={selectedAgent}
                    onChange={(e) => setSelectedAgent(e.target.value)}
                    label="pH Adjustment Agent"
                  >
                    {phAdjustmentAgents.map((agent, index) => {
                      const agentValue = `agent_${index}`;
                      return (
                        <MenuItem key={agent.name} value={agentValue}>
                          {agent.name} ({agent.type})
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                {/* Temporarily removed advanced options to fix import issue */}

                <Button
                  variant="contained"
                  onClick={calculatePHAdjustment}
                  disabled={calculating || !selectedAgent}
                  startIcon={<CalculateIcon />}
                >
                  {calculating ? 'Calculating...' : 'Calculate pH Adjustment'}
                </Button>
              </Box>
            </Grid>

            {/* Results */}
            <Grid xs={12} md={6}>
              {adjustmentResult && (
                <Box>
                  <Typography variant="h6" gutterBottom color="primary">
                    Adjustment Results
                  </Typography>

                  <Alert 
                    severity={adjustmentResult.warnings.length === 0 ? 'success' : 'warning'}
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="body2">
                      <strong>Required:</strong> {adjustmentResult.volumeRequired.toFixed(2)} mL of {adjustmentResult.requiredAgent.name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Final pH:</strong> {adjustmentResult.finalPH.toFixed(2)} 
                      (Target: {adjustmentResult.targetPH.toFixed(2)})
                    </Typography>
                  </Alert>

                  {adjustmentResult.warnings.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        <strong>Warnings:</strong>
                      </Typography>
                      {adjustmentResult.warnings.map((warning, index) => (
                        <Typography key={index} variant="body2">• {warning}</Typography>
                      ))}
                    </Alert>
                  )}

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Parameter</TableCell>
                          <TableCell align="right">Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Agent Volume</TableCell>
                          <TableCell align="right">{adjustmentResult.volumeRequired.toFixed(2)} mL</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Buffer Capacity</TableCell>
                          <TableCell align="right">{adjustmentResult.bufferCapacity.toFixed(2)} mmol/L/pH</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Agent Cost</TableCell>
                          <TableCell align="right">
                            ${((adjustmentResult.volumeRequired / 1000) * adjustmentResult.requiredAgent.cost).toFixed(3)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>pH Change</TableCell>
                          <TableCell align="right">
                            {(adjustmentResult.finalPH - adjustmentResult.currentPH).toFixed(2)} units
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Professional Interpretation */}
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      <strong>Professional Interpretation:</strong>
                    </Typography>
                    <Typography variant="body2">
                      • <strong>Volume Context:</strong> {adjustmentResult.volumeRequired.toFixed(2)} mL for {solutionVolume}L stock solution
                      ({((adjustmentResult.volumeRequired / solutionVolume) / 10).toFixed(4)}% of total volume)
                    </Typography>
                    <Typography variant="body2">
                      • <strong>Precision Level:</strong> {adjustmentResult.volumeRequired < 1 ? 'Micro-dosing' : adjustmentResult.volumeRequired < 5 ? 'Precision adjustment' : 'Standard dosing'}
                      - {adjustmentResult.volumeRequired < 1 ? 'requires accurate pipetting' : 'suitable for commercial application'}
                    </Typography>
                    <Typography variant="body2">
                      • <strong>Cost Efficiency:</strong> ${((adjustmentResult.volumeRequired / 1000) * adjustmentResult.requiredAgent.cost).toFixed(4)} per tank
                      (${(((adjustmentResult.volumeRequired / 1000) * adjustmentResult.requiredAgent.cost) * 365).toFixed(2)} annually for daily preparation)
                    </Typography>
                    <Typography variant="body2">
                      • <strong>Application Method:</strong> {adjustmentResult.volumeRequired < 0.5 ? 'Use precision pipette or syringe' :
                        adjustmentResult.volumeRequired < 2 ? 'Use graduated pipette or small measuring cylinder' :
                        'Use standard measuring equipment'}
                    </Typography>
                    <Typography variant="body2">
                      • <strong>Safety Level:</strong> {Math.abs(adjustmentResult.finalPH - adjustmentResult.currentPH) < 0.5 ? 'Low risk' :
                        Math.abs(adjustmentResult.finalPH - adjustmentResult.currentPH) < 1.0 ? 'Moderate risk' : 'High risk'} pH change
                      - {Math.abs(adjustmentResult.finalPH - adjustmentResult.currentPH) < 0.5 ? 'safe for direct addition' : 'consider gradual addition'}
                    </Typography>
                  </Alert>

                  {/* A/B Tank System Context */}
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      <strong>A/B Tank System Context:</strong>
                    </Typography>
                    <Typography variant="body2">
                      • <strong>Per Tank Requirement:</strong> {adjustmentResult.volumeRequired.toFixed(2)} mL per {solutionVolume}L tank
                    </Typography>
                    <Typography variant="body2">
                      • <strong>Total for A+B System:</strong> Up to {(adjustmentResult.volumeRequired * 2).toFixed(2)} mL
                      (if both tanks require adjustment)
                    </Typography>
                    <Typography variant="body2">
                      • <strong>Typical Application:</strong> Usually only one tank needs pH adjustment in A/B systems
                    </Typography>
                    <Typography variant="body2">
                      • <strong>Working Solution:</strong> When diluted to working strength (1:{dilutionRatio || 100}),
                      final pH will be approximately {adjustmentResult.finalPH.toFixed(1)}
                    </Typography>
                    <Typography variant="body2">
                      • <strong>Monitoring:</strong> Check pH after mixing A+B tanks at working dilution ratio
                    </Typography>
                  </Alert>

                  {/* Proceed to Recipe Export Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => {
                        if (adjustmentResult && onPHAdjustmentCalculated) {
                          onPHAdjustmentCalculated(adjustmentResult);
                        }
                      }}
                      startIcon={<TuneIcon />}
                    >
                      Proceed to Recipe Export
                    </Button>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default HendersonHasselbalchCalculator;
